import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { RouteComponentProps, Link, useHistory } from 'react-router-dom';
import {
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  Breadcrumbs
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MaterialTable, {
  MTableToolbar
} from 'material-table';

import AdminLayout from '../../../../../layouts/AdminLayout';
import Submission, { Log } from '../../../../../models/Submission';
import { GenericModal } from '../../../../../components/Modal';
import { AssignmentSpecSummary } from '../../../../../components/AssignmentSpecSummary';
import Course from '../../../../../models/Course';
import CourseWork from '../../../../../models/CourseWork';
import Student from '../../../../../models/Student';
import StudentSubmission from '../../../../../models/StudentSubmission';
import { useDialog } from '../../../../../context/Dialog';
import useUrlQuery from '../../../../../hooks/useUrlQuery';
import useRouteScrollMemory from '../../../../../hooks/useRouteScrollMemory';
import { TitleHeader } from '../../../../../components/Headers';
import utils from '../../../../../libs/utils';

const useStyles = makeStyles((theme) => ({
  column: {
    flexBasis: '70%',
  },
  tableContainer: {
    width: '100%'
  },
  toolbar: {
    '& .MuiToolbar-root': {
      paddingLeft: '4px'
    }
  },
  stateText: {
    color: 'grey',
    textTransform: 'capitalize'
  },
  disabledAccordion: {
    '& .MuiAccordionSummary-root': {
      cursor: 'default',
      pointerEvents: 'none',

      '& > *:nth-child(2), & > *:first-child > *:first-child': {
        opacity: 0.38
      }
    },
    '& a': {
      pointerEvents: 'all'
    }
  }
}));

const AssociateCourseWork = (props: {courseWork: CourseWork, onAssociated: (cw: CourseWork) => any}) => {
  const { courseWork, onAssociated } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { alert, confirm } = useDialog();

  const associate = async () => {
    try {
      setIsLoading(true);

      const confirmMessage = 'You are about to associate the assignment to the platform, you need to manually delete the existing one after. Do you wish to continue?';
      const confirmed = await confirm(confirmMessage,{
        title: 'Confirm Association',
        confirmText: 'Continue',
        cancelText: 'Cancel'
      });

      if (confirmed) {
        onAssociated(await courseWork.associate());
      }
    }
    catch (e) {
      await alert(utils.getRequestErrorMessage(e), {
        title: 'Associate Error'
      });
    }
    finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      type="button"
      size="small"
      color="primary"
      variant="outlined"
      startIcon={isLoading ? (<CircularProgress size={14} color="inherit" />) : ''}
      disabled={isLoading}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        (async () => await associate())();
      }}>
      {isLoading ? 'Associating' : 'Associate'}
    </Button>
  );
};

const AssignmentStudentTable = (props: {active: boolean, courseWork: CourseWork, students: Student[], submissionLogs: Log[]}) => {
  const { active, courseWork, students, submissionLogs } = props;
  const [initialized, setInitialized] = useState<boolean>(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const classes = useStyles();
  const { query, setQuery } = useUrlQuery();

  const data = submissions ? submissions.map((submission) => {
    return {
      id: submission.id,
      name: submission.fullname,
      grade: String(submission.grade || ''),
      submittedData:  submission?.files || [] ,
      link: `/admin/courses/${courseWork.courseId}/assignments/${courseWork.id}/students/${submission.id}`
    };
    }):[];

  const enablePagination = query.pagination !== 'no';

  const hasNewSubmission = (courseID: string, studentID: string, assignmentID: string): boolean => {
    return submissionLogs.some(
      (log: Log): Boolean =>
        log.courseID === courseID &&
        log.personID === studentID &&
        log.assignmentID === assignmentID
    );
  };

  const loadStudentSubmissions = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      const moodleCourseAssignmentSubmissions = await CourseWork.getSubmissions({
        assignmentId: courseWork.id
      });
      setSubmissions(moodleCourseAssignmentSubmissions);
      // setSubmissions(await StudentSubmission.find({
      //   courseId: courseWork.courseId,
      //   courseWorkId: courseWork.id
      // }));
    }
    catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    }
    finally {
      setIsLoading(false);
    }
  };

  if (!active)
    return (<></>);

  if (!initialized) {
    setInitialized(true);
    (async () => await loadStudentSubmissions())();
  }

  if (errorMessage)
    return (
      <div className="text-center">
        <Typography variant="h5">Something went wrong.</Typography>
        <p>{ errorMessage }</p>

        <Button
          type="button"
          onClick={loadStudentSubmissions}
          size="small"
          color="primary">
          Try Again
        </Button>
      </div>
    );

  return (
    <MaterialTable
      title={(
        <>
          <Switch
            checked={enablePagination}
            onChange={e =>setQuery({...query, pagination: e.target.checked ? '' : 'no'})}
            color="primary"
            name="Paginate"
          />
          Pagination
        </>
      )}
      isLoading={isLoading}
      data={data}
      columns={[
        {
          title: 'ID',
          field: 'id'
        },
        {
          title: 'Name',
          field: 'name',
          render: (row) => (
            <Link to={row.link}>{row.name}</Link>
          )
        },
        {
          title: 'Grade',
          field: 'grade',
          customSort: (a, b) => {
            const _a = Number((a.grade || '').split(' ')[0] || 0);
            const _b = Number((b.grade || '').split(' ')[0] || 0);
            return _a - _b;
          }
        },
        {
          title: 'Submitted',
          field: 'submittedData',
          sorting: false,
          render: (student) => {
            if (student.submittedData.length) {
              if (hasNewSubmission(courseWork.courseId, student.id, courseWork.id))
                return (
                  <div
                    style={{
                      height: 15,
                      width: 15,
                      borderRadius: 15,
                      backgroundColor: '#2196F3',
                      float: 'right',
                    }}
                  />
                );
              return '✓';
            }
            return '';
          }
        },
      ]}
      options={{
        thirdSortClick: false,
        pageSize: 10,
        pageSizeOptions: [5, 10, 20, 50],
        emptyRowsWhenPaging: false,
        search: true,
        paging: enablePagination,
        initialPage: Number(query.page || 1) - 1
      }}
      onChangePage={page => setQuery({...query, page: page + 1})}
      components={{
        Toolbar: props => (
          <div className={classes.toolbar}>
            <MTableToolbar {...props} style={{paddingLeft: 0}} />
          </div>
        )
      }}
    />
  );
};

const Assignments = (props: RouteComponentProps<{ course_id: string }>) => {
  const {course_id: courseId} = props.match.params;
  
  const history = useHistory();
  
  const [course, setCourse] = useState<Course|null>(null);
  const [courseWorks, setCourseWorks] = useState<CourseWork[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [submissionLogs, setSubmissionLogs] = useState([] as Log[]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSummaryOpen, setIsSummaryOpen] = useState(false as boolean);
  const [isAssociatingAll, setIsAssociatingAll] = useState<boolean>(false);

  const classes = useStyles();
  const { alert, confirm } = useDialog();
  const { query, setQuery } = useUrlQuery();
  const { saveScroll, restoreScroll } = useRouteScrollMemory();

  const associatedCourseWorks = courseWorks.filter(a => a.associatedWithDeveloper);


  const loadCourse = async () => {
    const resp = await Course.findById({
      courseId
    });
    setCourse(resp);
  };

  const loadCourseWorks = async () => {
    const resp = await CourseWork.getMoodleCourseAssignments({
      courseId
    });
    const sortedByName = resp.sort((a, b) => a.name.localeCompare(b.name));
    setCourseWorks(sortedByName);

  };

  const loadCourseStudents = async () => {
    setStudents(await Student.find({courseId: courseId}, {acceptCache: true}));
  };

  const loadSubmissionLogs = async () => {
    setSubmissionLogs(await Submission.getSubmissionLogs());
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      setCourse(null);
      setCourseWorks([]);
      setStudents([]);
      setSubmissionLogs([]);

      await Promise.all([
        loadCourse(),
        loadCourseWorks(),
        // loadCourseStudents(),
        // loadSubmissionLogs()
      ]);


    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
      restoreScroll();
    }
  };

  const setActiveCourseWorkId = (courseWorkId: string) => {
    setQuery({...query, courseWork: courseWorkId, page: 1});
  };

  const activeCourseWorkId = query.courseWork || '';


  useEffect(() => {
    (async () => await loadData())();
  }, []);

  useEffect(() => saveScroll, []);

  return (
    <AdminLayout>
      <GenericModal
        open={isSummaryOpen}
        onClose={() => setIsSummaryOpen(false)}
        header={
          <Typography variant="h6">Course Spec Summary | {courseId}</Typography>
        }>
        <AssignmentSpecSummary courseId={isSummaryOpen ? courseId : ''} courseWorks={courseWorks} />
      </GenericModal>

      <div className="py-5">
        {(() => {
          if (isLoading)
            return (
              <div className="text-center p-4">
                <CircularProgress color="primary" />
                <p className="m-0">Loading Assignments</p>
              </div>
            );

          if (errorMessage)
            return (
              <div className="text-center p-4">
                <h3 className="m-0">Error</h3>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={loadData}
                  size="small"
                  color="primary"
                >
                  Try Again
                </Button>
              </div>
            );

          return (
            <>
              <Grid container justify="space-between" alignItems="center">
                <Grid>
                  <TitleHeader
                    breadcrumb={
                      <Breadcrumbs aria-label="breadcrumb">
                        <Link to={Course.getListUrlPath('admin')}>Courses</Link>
                        <Typography>{course?.fullname || courseId}</Typography>
                        <Typography>Assignments</Typography>
                      </Breadcrumbs>
                    }
                  >
                    <Typography variant="h5">Assignments</Typography>
                  </TitleHeader>
                </Grid>
                <Grid>
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    onClick={() => setIsSummaryOpen(true)}>
                    View Spec Summary
                  </Button>
                </Grid>
              </Grid>

              <div className="pt-3">
                {courseWorks.map((courseWork) => (
                  <Accordion
                    key={courseWork.id}
                    // className={courseWork.state !== 'PUBLISHED' ? classes.disabledAccordion : ''}
                    expanded={activeCourseWorkId === courseWork.id}
                    onChange={() =>
                      setActiveCourseWorkId(courseWork.id === activeCourseWorkId ? '' : courseWork.id)
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <div className={classes.column}>
                        <span>{courseWork.name} &nbsp; </span>
                        <small>({courseWork.id})</small>
                        {/* <span>publish status should be here I suppose.</span> */}
                        {/* {courseWork.state !== 'PUBLISHED' && (
                          <span className={classes.stateText}> - {courseWork.state.toLowerCase()}</span>
                        )} */}
                      </div>
                      <div onClick={e => e.stopPropagation()}>
                        <Button
                          type="button"
                          size="small"
                          color="primary"
                          href={`#/admin/courses/${courseId}/assignments/${courseWork.id}/specs`}
                        >
                          Assign Specs
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          color="primary"
                          onClick={() => {
                              history.push({
                                pathname: `/admin/courses/${courseId}/specs/${courseWork.id}`,
                                state: {courseWork: {name: courseWork.name}},
                              });
                          }}
                        >
                          View Specs
                        </Button>
                      </div>
                    </AccordionSummary>
                    {(
                      <AccordionDetails>
                        <div className={classes.tableContainer}>
                          <AssignmentStudentTable
                            active={activeCourseWorkId === courseWork.id}
                            courseWork={{...courseWork, courseId} as CourseWork}
                            students={students}
                            submissionLogs={submissionLogs}
                          />
                        </div>
                      </AccordionDetails>
                    )}
                  </Accordion>
                ))}
              </div>
            </>
          );
        })()}
      </div>
    </AdminLayout>
  );
};

export default Assignments;
